import React from 'react';

import { Box, Collapse, Tooltip } from '@mui/material';

import { observer } from 'mobx-react';

import { ExpandLess, ExpandMore } from '@mui/icons-material';

import InfoIcon from '@mui/icons-material/Info';

import { StyledTextField } from 'components/Input/styles';

import { useStore } from 'utils/hooks/store';

import { colors } from 'theme/colors';

import { Section, SectionContainerHeader, TitleWrapper, Title, FieldLabel, FieldLabelRow, Description } from './styles';

const StageDetails = (): React.ReactElement => {
  const { pipelineDetails } = useStore();

  const stage = pipelineDetails.data?.stages.find(({ key }) => key === pipelineDetails.selectedStage);

  if (!stage) {
    return <></>;
  }

  const [expanded, setExpanded] = React.useState(true);
  const [nameError, setNameError] = React.useState('');

  const handleExpandClick = (): void => {
    setExpanded((prevState) => !prevState);
  };

  const handleNameChange = (newName: string): void => {
    if (newName.length > 255) {
      setNameError('Stage name must be 255 characters or less');
    } else {
      setNameError('');
    }
    pipelineDetails.updateStageData(stage.key, { name: newName });
  };

  return (
    <Section>
      <SectionContainerHeader $expanded={expanded}>
        <TitleWrapper onClick={handleExpandClick}>
          {expanded ? (
            <ExpandLess aria-labelledby="show less" fillOpacity="0.54" />
          ) : (
            <ExpandMore aria-labelledby="show more" fillOpacity="0.54" />
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Title id="section-title">Stage Details</Title>
            <Description>Configure the stage name, and guidance notes for users.</Description>
          </Box>
        </TitleWrapper>
      </SectionContainerHeader>
      <Collapse in={expanded} sx={{ padding: expanded ? '0 16px 16px' : '0' }}>
        <div>
          <FieldLabelRow>
            <FieldLabel>Stage name</FieldLabel>
          </FieldLabelRow>
          <StyledTextField
            value={stage.name}
            placeholder="Stage Name"
            onChange={(e) => handleNameChange(e.target.value)}
            fullWidth
            error={!!nameError}
            helperText={nameError}
          />
        </div>
        <div>
          <FieldLabelRow>
            <FieldLabel>API name</FieldLabel>
            <Tooltip
              componentsProps={{
                tooltip: {
                  sx: {
                    color: colors.common.white,
                    padding: '0px 8px',
                    fontSize: '14px',
                    borderRadius: '4px',
                    fontWeight: 400,
                  },
                },
              }}
              title="Used to read from and write to the field from the API"
              placement="right"
            >
              <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </Tooltip>
          </FieldLabelRow>
          <StyledTextField value={stage.key} placeholder="API name" disabled fullWidth />
        </div>

        <div>
          <FieldLabelRow>
            <FieldLabel>Stage guidance</FieldLabel>
            <Tooltip
              componentsProps={{
                tooltip: {
                  sx: {
                    color: colors.common.white,
                    padding: '0px 8px',
                    fontSize: '14px',
                    borderRadius: '4px',
                    fontWeight: 400,
                    margin: '16px 0 4px 0',
                  },
                },
              }}
              title="This guidance text will be shown to users in the event record when the event is at this stage. Use it to explain what actions are expected or how to handle the event at this point in the process."
              placement="right"
            >
              <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </Tooltip>
          </FieldLabelRow>
          <StyledTextField
            value={stage.guidance}
            placeholder="Write guidance here..."
            onChange={(e) => pipelineDetails.updateStageData(stage.key, { guidance: e.target.value })}
            multiline
            minRows={8}
            maxRows={20}
            fullWidth
          />
        </div>
      </Collapse>
    </Section>
  );
};

export default observer(StageDetails);
